import { useState, useEffect, useRef } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { startLive, stopLive } from './live/live';
import { RoleProvider, useRole } from './context/RoleContext';
import { ThemeProvider } from './context/ThemeContext';
import { AccentProvider } from './context/AccentContext';
import { SearchProvider } from './context/SearchContext';
import { PageFiltersProvider } from './context/PageFiltersContext';
import { NavigationGuardProvider, useNavigationGuardRef } from './context/NavigationGuardContext';
import { I18nProvider, useT } from './i18n';
import { api } from './api/client';
import { ConfirmModal } from './components/ConfirmModal';
import { LoginPage } from './pages/LoginPage';
import { PortfolioPage } from './pages/PortfolioPage';
import { HomePage } from './pages/HomePage';
import { PipelinePage } from './pages/PipelinePage';
import { InboxPage } from './pages/InboxPage';
import { ListPage } from './pages/ListPage';
import { PeoplePage } from './pages/PeoplePage';
import { InfrastructurePage } from './pages/InfrastructurePage';
import { DeliverablesPage } from './pages/DeliverablesPage';
import { AdminPage } from './pages/AdminPage';
import { SubmitProjectPage } from './pages/SubmitProjectPage';
import { ProfilePage } from './pages/ProfilePage';
import { TasksPage } from './pages/TasksPage';
import { CalendarPage } from './pages/CalendarPage';
import { KanbanBoardsPage } from './pages/KanbanBoardsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { CouncilPage } from './pages/CouncilPage';
import { GerancePage } from './pages/GerancePage';
import { ProposalsPage } from './proposals/ProposalsPage';
import { TimelineTaches } from './proposals/timeline-taches/TimelineTaches';
import { AppShell } from './components/layout/AppShell';
import { DevEnvBanner } from './components/layout/DevEnvBanner';

type Page = 'home' | 'portfolio' | 'pipeline' | 'inbox' | 'projects' | 'people' | 'infrastructure' | 'deliverables' | 'admin' | 'submit' | 'profile' | 'tasks' | 'tasks-calendar' | 'tasks-kanban' | 'tasks-planning' | 'project' | 'council' | 'gerance' | 'proposals';

const ADMIN_ONLY_PAGES: Page[] = ['inbox', 'projects', 'people', 'infrastructure', 'deliverables', 'admin', 'council'];

function AppContent() {
  const { user, loading } = useAuth();
  const { isAdmin, isDirection, canSeeTasks, canSeeProposals, loading: roleLoading } = useRole();
  const { t, setLang } = useT();
  const [currentPage, setCurrentPage] = useState<Page>('home');
  // Which project the project page shows. Going from one project straight to
  // another (sidebar "Projets récents", links between projects) only changes the
  // id in the hash — the page stays 'project' — so the page is keyed on it and
  // rebuilt for the new project.
  const [projectKey, setProjectKey] = useState('');
  const isDirtyRef = useNavigationGuardRef();
  const prevHashRef = useRef(window.location.hash);
  const bypassGuardRef = useRef(false);
  const [pendingHash, setPendingHash] = useState<string | null>(null);

  // Apply the user's server-stored language preference (canonical, wins over
  // localStorage). Users who never chose keep the local/default value ('fr').
  useEffect(() => {
    if (!user) return;
    api.getProfile()
      .then((p) => {
        if (p.lang === 'fr' || p.lang === 'en') setLang(p.lang);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Live updates and presence while logged in (live/live.ts).
  useEffect(() => {
    if (!user) return;
    startLive();
    return () => stopLive();
  }, [user?.id]);

  // Hash-based routing with role-based guards
  useEffect(() => {
    const handleHashChange = () => {
      const newHash = window.location.hash;

      // A page with unsaved changes is mounted — pause the navigation (revert
      // the URL, since the browser already applied it) and ask before leaving.
      // beforeunload (registered by the page itself) covers tab close/refresh;
      // this covers in-app hash navigation and back/forward.
      if (isDirtyRef.current && !bypassGuardRef.current && newHash !== prevHashRef.current) {
        window.history.replaceState(null, '', prevHashRef.current || '#home');
        setPendingHash(newHash);
        return;
      }
      bypassGuardRef.current = false;
      prevHashRef.current = newHash;

      // Role data isn't in yet (e.g. a fresh tab opened straight to a deep
      // link) — defer the admin/tasks gates below until isAdmin/canSeeTasks
      // reflect the real role, instead of the still-loading default of
      // false, which would bounce a valid admin-only link (e.g. #gerance)
      // to portfolio before we actually know. AppContent shows a spinner
      // for the duration, and this effect re-runs once roleLoading clears.
      if (roleLoading) return;

      // No page in the URL (just logged in, logo, bare address): the home page.
      const raw = newHash.slice(1) || 'home';
      let page = raw.split('?')[0] as Page;
      // Backward compat: #list -> projects
      if (page === 'list' as any) page = 'projects';
      // Admin-only pages: admin gets all, direction can access inbox for edit/changes
      if (!isAdmin && ADMIN_ONLY_PAGES.includes(page)) {
        const hash = window.location.hash;
        const directionAllowed = isDirection && page === 'inbox' &&
          (hash.includes('edit=') || hash.includes('tab=changes'));
        if (!directionAllowed) {
          window.location.hash = 'home';
          return;
        }
      }
      // Tasks pages: only if user can see tasks
      if ((page === 'tasks' || page === 'tasks-calendar' || page === 'tasks-kanban' || page === 'tasks-planning') && !canSeeTasks) {
        window.location.hash = 'home';
        return;
      }
      // Gérance: admins (full portfolio) plus anyone with project assignments
      // (their own projects only — the page scopes itself). Same gate as tasks.
      if (page === 'gerance' && !canSeeTasks) {
        window.location.hash = 'home';
        return;
      }
      // Proposals page: named allow-list only (server utils/proposals.ts).
      if (page === 'proposals' && !canSeeProposals) {
        window.location.hash = 'home';
        return;
      }
      setCurrentPage(page);
      setProjectKey(page === 'project' ? new URLSearchParams(raw.split('?')[1] || '').get('id') ?? '' : '');
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [isAdmin, isDirection, canSeeTasks, canSeeProposals, roleLoading, isDirtyRef]);

  const navigate = (page: Page) => {
    window.location.hash = page;
  };

  const confirmLeave = () => {
    if (!pendingHash) return;
    bypassGuardRef.current = true;
    isDirtyRef.current = false;
    window.location.hash = pendingHash;
    setPendingHash(null);
  };
  const cancelLeave = () => setPendingHash(null);

  if (loading || roleLoading) {
    return (
      <div className="login-page">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <HomePage />;
      case 'portfolio':
        return <PortfolioPage />;
      case 'pipeline':
        return <PipelinePage />;
      case 'inbox':
        return <InboxPage />;
      case 'projects':
        return <ListPage />;
      case 'people':
        return <PeoplePage />;
      case 'infrastructure':
        return <InfrastructurePage />;
      case 'deliverables':
        return <DeliverablesPage />;
      case 'admin':
        return <AdminPage />;
      case 'submit':
        return <SubmitProjectPage />;
      case 'profile':
        return <ProfilePage />;
      case 'tasks':
        return <TasksPage />;
      case 'tasks-calendar':
        return <CalendarPage />;
      case 'tasks-kanban':
        return <KanbanBoardsPage />;
      case 'tasks-planning':
        // [Proposition: timeline-taches] The project task timeline replaces the
        // personal deadline view, for every user.
        return <TimelineTaches />;
      case 'project':
        return <ProjectDetailPage key={projectKey} />;
      case 'council':
        return <CouncilPage />;
      case 'proposals':
        return <ProposalsPage />;
      case 'gerance':
        return <GerancePage />;
      default:
        return <HomePage />;
    }
  };

  return (
    <ThemeProvider>
      <SearchProvider>
        <PageFiltersProvider>
          <AccentProvider>
            <AppShell currentPage={currentPage} onNavigate={navigate}>
              {renderPage()}
            </AppShell>
            <ConfirmModal
              open={pendingHash != null}
              title={t('guard.title')}
              message={t('guard.message')}
              confirmLabel={t('guard.leave')}
              destructive
              onConfirm={confirmLeave}
              onCancel={cancelLeave}
            />
          </AccentProvider>
        </PageFiltersProvider>
      </SearchProvider>
    </ThemeProvider>
  );
}

export function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <RoleProvider>
          <NavigationGuardProvider>
            <AppContent />
          </NavigationGuardProvider>
        </RoleProvider>
      </AuthProvider>
      <DevEnvBanner />
    </I18nProvider>
  );
}
